import { CategoryTree } from '@/components/category-tree';
import { LocationFilterField, resolveNextRadius } from '@/components/location-filter-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { type FilterOptions, type ListingFilters } from '@/types';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

interface AppliedFilter {
    type: keyof ListingFilters;
    value: string | number;
    display: string;
}

type SectionKey = 'category' | 'location' | 'service' | 'hours' | 'rating' | 'verified';

const MOBILE_COLLAPSED_SECTIONS: Record<SectionKey, boolean> = {
    category: false,
    location: false,
    service: false,
    hours: false,
    rating: false,
    verified: false,
};

const DESKTOP_DEFAULT_SECTIONS: Record<SectionKey, boolean> = {
    category: true,
    location: true,
    service: false,
    hours: false,
    rating: false,
    verified: false,
};

const MOBILE_BREAKPOINT = 1024;

/** Notas mínimas oferecidas (spec 5.1: 4 ou mais, 4,5 ou mais). */
export const RATING_MIN_OPTIONS = [4, 4.5];

/** Filtros de empresa vazios (CN-200), para montar e limpar o estado. */
export const EMPTY_LISTING_ATTRIBUTE_FILTERS: Pick<ListingFilters, 'service' | 'openSaturday' | 'openSunday' | 'openNow' | 'ratingMin' | 'verified'> = {
    service: '',
    openSaturday: false,
    openSunday: false,
    openNow: false,
    ratingMin: null,
    verified: false,
};

/** Quantos filtros de funcionamento/avaliação/serviço estão ativos (contador "Filtros (n)"). */
export const countListingAttributeFilters = (filters: ListingFilters): number =>
    [filters.service.trim() !== '', filters.openSaturday, filters.openSunday, filters.openNow, filters.ratingMin !== null, filters.verified].filter(Boolean)
        .length;

const getInitialExpandedState = () => {
    if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT) {
        return MOBILE_COLLAPSED_SECTIONS;
    }

    return DESKTOP_DEFAULT_SECTIONS;
};

interface FilterSectionProps {
    title: string;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}

// Declared outside the sidebar (as in boat-filter-sidebar): a component created on every render would remount its
// children and the "Serviço" input would lose the focus at each keystroke.
const FilterSection: React.FC<FilterSectionProps> = ({ title, expanded, onToggle, children }) => (
    <div className="mb-4 border-b border-gray-200 pb-4 last:mb-0 last:border-b-0 last:pb-0">
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="flex w-full items-center justify-between py-2 text-left font-medium text-gray-700 transition-colors hover:text-gray-900"
        >
            {title}
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {expanded && <div className="mt-3 space-y-3">{children}</div>}
    </div>
);

interface ListingFilterSidebarProps {
    filters: ListingFilters;
    onFiltersChange: (filters: ListingFilters) => void;
    filterOptions: FilterOptions;
    className?: string;
    /** Opções e padrão do raio de distância do módulo (config/search.php). */
    radiusOptions?: number[];
    defaultRadius?: number;
    /** Dentro da gaveta do celular (CN-200): sem o botão "Filtros (n)" e com as seções já visíveis. */
    alwaysExpanded?: boolean;
}

export const ListingFilterSidebar: React.FC<ListingFilterSidebarProps> = ({
    filters,
    onFiltersChange,
    filterOptions,
    className = '',
    radiusOptions = [],
    defaultRadius = 0,
    alwaysExpanded = false,
}) => {
    const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>(() =>
        alwaysExpanded ? { ...DESKTOP_DEFAULT_SECTIONS, service: true, hours: true, rating: true, verified: true } : getInitialExpandedState(),
    );
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(!alwaysExpanded);
    const [serviceText, setServiceText] = useState(filters.service);

    const categoryLookup = useMemo(() => {
        const lookup: Record<number, string> = {};

        for (const category of filterOptions.categories) {
            lookup[category.id] = category.name;
        }

        return lookup;
    }, [filterOptions.categories]);

    useEffect(() => {
        setServiceText(filters.service);
    }, [filters.service]);

    // Update state when window resizes (only for mobile to desktop transition)
    useEffect(() => {
        if (alwaysExpanded) {
            return;
        }

        const handleResize = () => {
            if (window.innerWidth >= MOBILE_BREAKPOINT) {
                setExpandedSections(DESKTOP_DEFAULT_SECTIONS);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [alwaysExpanded]);

    const toggleSection = (section: SectionKey) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    const appliedFilters = useMemo<AppliedFilter[]>(() => {
        const nextFilters: AppliedFilter[] = [];

        for (const categoryId of filters.category) {
            nextFilters.push({
                type: 'category',
                value: categoryId,
                display: categoryLookup[Number(categoryId)] || `Categoria ${categoryId}`,
            });
        }

        if (filters.location) {
            nextFilters.push({
                type: 'location',
                value: filters.location.id,
                display: filters.location.label,
            });
        }

        if (filters.service.trim()) nextFilters.push({ type: 'service', value: filters.service, display: `Serviço: ${filters.service}` });
        if (filters.openSaturday) nextFilters.push({ type: 'openSaturday', value: 1, display: 'Abre sábado' });
        if (filters.openSunday) nextFilters.push({ type: 'openSunday', value: 1, display: 'Abre domingo' });
        if (filters.openNow) nextFilters.push({ type: 'openNow', value: 1, display: 'Aberta agora' });
        if (filters.ratingMin !== null) nextFilters.push({ type: 'ratingMin', value: filters.ratingMin, display: `Nota ${filters.ratingMin}+` });
        if (filters.verified) nextFilters.push({ type: 'verified', value: 1, display: 'Verificada' });

        return nextFilters;
    }, [categoryLookup, filters]);

    const commitService = () => {
        const next = serviceText.trim().slice(0, 60);

        if (next !== filters.service) {
            onFiltersChange({ ...filters, service: next });
        }
    };

    const toggleFlag = (key: 'openSaturday' | 'openSunday' | 'openNow' | 'verified') => onFiltersChange({ ...filters, [key]: !filters[key] });

    const sectionProps = (sectionKey: SectionKey) => ({ expanded: expandedSections[sectionKey], onToggle: () => toggleSection(sectionKey) });

    return (
        <div className={cn('space-y-6 rounded-xl bg-white px-2 lg:p-6', className)}>
            {/* Mobile Toggle Button - Only visible on mobile */}
            {!alwaysExpanded && (
                <button
                    type="button"
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    className="flex w-full items-center justify-between py-2 text-left font-semibold text-gray-900 transition-colors hover:text-gray-700 lg:hidden"
                >
                    <span>Filtros {appliedFilters.length > 0 && `(${appliedFilters.length})`}</span>
                    {isSidebarCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
                </button>
            )}

            {/* Collapsible content - hidden on mobile when collapsed, always visible on desktop */}
            <div className={`space-y-6 ${isSidebarCollapsed ? 'hidden lg:block' : 'block'}`}>
                {/* Filter Categories */}
                <div className="space-y-1">
                    <FilterSection title="Categoria" {...sectionProps('category')}>
                        <CategoryTree
                            categories={filterOptions.categories}
                            selectedCategories={filters.category}
                            onSelectionChange={(nextCategories) => {
                                onFiltersChange({ ...filters, category: nextCategories });
                            }}
                        />
                    </FilterSection>

                    <FilterSection title="Localização" {...sectionProps('location')}>
                        <LocationFilterField
                            searchType="empresas"
                            location={filters.location}
                            radius={filters.radius}
                            radiusOptions={radiusOptions}
                            onLocationChange={(nextLocation) =>
                                onFiltersChange({
                                    ...filters,
                                    location: nextLocation,
                                    radius: resolveNextRadius(filters.location, filters.radius, nextLocation, defaultRadius),
                                })
                            }
                            onRadiusChange={(nextRadius) => onFiltersChange({ ...filters, radius: nextRadius })}
                        />
                    </FilterSection>

                    <FilterSection title="Serviço" {...sectionProps('service')}>
                        <div className="space-y-1">
                            <Label htmlFor="listing-service-input" className="text-xs text-gray-600">
                                Procura nos serviços e na descrição
                            </Label>
                            <Input
                                id="listing-service-input"
                                name="service"
                                type="text"
                                autoComplete="off"
                                maxLength={60}
                                placeholder="Ex.: motor de popa, vaga seca"
                                value={serviceText}
                                onChange={(event) => setServiceText(event.target.value)}
                                onBlur={commitService}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        commitService();
                                        event.currentTarget.blur();
                                    }
                                }}
                            />
                        </div>
                    </FilterSection>

                    <FilterSection title="Funcionamento" {...sectionProps('hours')}>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-1" role="group" aria-label="Funcionamento">
                            {(
                                [
                                    ['openSaturday', 'Abre aos sábados'],
                                    ['openSunday', 'Abre aos domingos'],
                                    ['openNow', 'Aberta agora'],
                                ] as const
                            ).map(([key, label]) => (
                                <Button
                                    key={key}
                                    type="button"
                                    size="sm"
                                    variant={filters[key] ? 'default' : 'outline'}
                                    aria-pressed={filters[key]}
                                    onClick={() => toggleFlag(key)}
                                    className={cn('justify-start px-3', filters[key] && 'bg-[#0f766e] hover:bg-[#0b5f58]')}
                                >
                                    <span className="truncate">{label}</span>
                                </Button>
                            ))}
                        </div>
                    </FilterSection>

                    <FilterSection title="Avaliação" {...sectionProps('rating')}>
                        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Nota mínima">
                            {RATING_MIN_OPTIONS.map((option) => {
                                const selected = filters.ratingMin === option;

                                return (
                                    <Button
                                        key={option}
                                        type="button"
                                        size="sm"
                                        variant={selected ? 'default' : 'outline'}
                                        aria-pressed={selected}
                                        onClick={() => onFiltersChange({ ...filters, ratingMin: selected ? null : option })}
                                        className={cn('px-2', selected && 'bg-[#0f766e] hover:bg-[#0b5f58]')}
                                    >
                                        Nota {option.toLocaleString('pt-BR')} ou mais
                                    </Button>
                                );
                            })}
                        </div>
                    </FilterSection>

                    <FilterSection title="Empresa verificada" {...sectionProps('verified')}>
                        <Button
                            type="button"
                            size="sm"
                            variant={filters.verified ? 'default' : 'outline'}
                            aria-pressed={filters.verified}
                            onClick={() => toggleFlag('verified')}
                            className={cn('w-full justify-start px-3', filters.verified && 'bg-[#0f766e] hover:bg-[#0b5f58]')}
                        >
                            Só empresas verificadas
                        </Button>
                    </FilterSection>
                </div>
            </div>
        </div>
    );
};
